/**
 * Buckets `CalendarEvent`s by the local calendar day they start on. This is
 * the fix for the bug this whole feature started from: `CalendarScreen.tsx`
 * used to group with `isoDate.slice(0, 10)`, which reads the UTC day baked
 * into the ISO string, not the viewer's local day. Built on Unit 1's
 * `calendarDate.ts` primitives — still no React, no I/O.
 */
import type { CalendarEvent } from '../../../types/calendar';
import {
  compareLocalDate,
  type LocalDate,
  localDateKey,
  localDayKey,
  toLocalDate,
} from './calendarDate';

export interface LocalDayEvents {
  date: LocalDate;
  events: CalendarEvent[];
}

/** Sorted by date; each day's events sorted by start time. */
export function groupEventsByLocalDay(events: CalendarEvent[], timeZone: string): LocalDayEvents[] {
  const byDay = new Map<string, LocalDayEvents>();

  for (const event of events) {
    const date = toLocalDate(event.startsAt, timeZone);
    const key = localDateKey(date);
    const existing = byDay.get(key);
    if (existing) {
      existing.events.push(event);
    } else {
      byDay.set(key, { date, events: [event] });
    }
  }

  return [...byDay.values()]
    .sort((a, b) => compareLocalDate(a.date, b.date))
    .map(({ date, events: dayEvents }) => ({
      date,
      events: [...dayEvents].sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
    }));
}

/** Same bucketing as `groupEventsByLocalDay`, as a `YYYY-MM-DD`-keyed map for
 * the per-cell lookups a grid needs (month grid: up to 42 cells per render). */
export function indexEventsByLocalDay(
  events: CalendarEvent[],
  timeZone: string,
): Map<string, CalendarEvent[]> {
  const map = new Map<string, CalendarEvent[]>();
  for (const { date, events: dayEvents } of groupEventsByLocalDay(events, timeZone)) {
    map.set(localDateKey(date), dayEvents);
  }
  return map;
}

/**
 * Whether `event` touches the local day `dateKey` (`YYYY-MM-DD`) in
 * `timeZone` — true if it STARTS or ENDS on that day, not just starts on it.
 * This is deliberately a different question from `groupEventsByLocalDay`'s
 * "which single day owns this event" (start day only, for grid/list
 * assignment): a Day or Week timeline needs to know every day an event is
 * visible on, and bucketing by start day alone would silently drop the
 * after-midnight portion of an overnight event from the day it spills onto.
 */
export function eventTouchesLocalDay(
  event: CalendarEvent,
  dateKey: string,
  timeZone: string,
): boolean {
  return (
    localDayKey(event.startsAt, timeZone) === dateKey ||
    localDayKey(event.endsAt, timeZone) === dateKey
  );
}
